import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Calendar, CalendarClock, Users, Car, ClipboardCheck,
  Wrench, FileText, Receipt, CreditCard, Sparkles, UserSquare2, Boxes,
  BarChart3, Settings, LogOut, Zap, DollarSign, PenSquare, KeyRound, Bell,
  Calculator, Images, Inbox,
} from "lucide-react";
import { useCrmAuth } from "../CrmAuth";
import { CRM_API } from "../api";
import ChangePasswordModal from "./ChangePasswordModal";

const NAV = [
  { to: "/crm", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/crm/quick-quote", label: "Quick Quote", icon: Calculator, highlight: true },
  { to: "/crm/leads", label: "Leads", icon: Inbox, badge: true },
  { to: "/crm/calendar", label: "Calendar", icon: Calendar },
  { to: "/crm/appointments", label: "Appointments", icon: CalendarClock },
  { to: "/crm/customers", label: "Customers", icon: Users },
  { to: "/crm/follow-ups", label: "Follow-Ups", icon: Bell },
  { to: "/crm/vehicles", label: "Vehicles", icon: Car },
  { to: "/crm/check-in", label: "Check-In", icon: Zap, highlight: true },
  { to: "/crm/inspections", label: "Inspections", icon: ClipboardCheck },
  { to: "/crm/work-orders", label: "Work Orders", icon: Wrench },
  { to: "/crm/estimates", label: "Estimates", icon: FileText },
  { to: "/crm/invoices", label: "Invoices", icon: Receipt },
  { to: "/crm/payments", label: "Payments", icon: CreditCard },
  { to: "/crm/services", label: "Services", icon: Sparkles },
  { to: "/crm/pricing", label: "Pricing", icon: DollarSign, roles: ["owner", "manager"] },
  { to: "/crm/blog", label: "Blog", icon: PenSquare, roles: ["owner"] },
  { to: "/crm/site-media", label: "Site Photos", icon: Images, roles: ["owner"] },
  { to: "/crm/employees", label: "Employees", icon: UserSquare2 },
  { to: "/crm/inventory", label: "Inventory", icon: Boxes },
  { to: "/crm/reports", label: "Reports", icon: BarChart3 },
  { to: "/crm/settings", label: "Settings", icon: Settings },
];

export default function CrmSidebar({ open, onClose }) {
  const { user, logout } = useCrmAuth();
  const nav = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [leadCount, setLeadCount] = useState(0);

  // Pending website leads badge — same source as the Leads page
  useEffect(() => {
    let stop = false;
    const tick = () =>
      CRM_API.get("/quotes")
        .then(({ data }) => {
          if (stop) return;
          setLeadCount((Array.isArray(data) ? data : [])
            .filter((q) => q.status === "pending" && !q.appointment_id).length);
        })
        .catch(() => {});
    tick();
    const t = setInterval(tick, 45000);
    return () => { stop = true; clearInterval(t); };
  }, []);
  return (
    <>
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
      {/* Mobile backdrop */}
      <div
        className={`fixed inset-0 bg-black/60 z-40 lg:hidden transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />
      <aside
        data-testid="crm-sidebar"
        className={`fixed lg:sticky top-0 left-0 h-screen w-72 z-50 bg-[#0e0e12] border-r border-white/10 flex flex-col transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Brand */}
        <button
          type="button"
          onClick={() => { nav("/crm"); onClose?.(); }}
          className="flex items-center gap-3 px-6 py-3.5 border-b border-white/10 hover:bg-white/5 transition-colors"
        >
          <img
            src="/logo-mark.png"
            alt="WTD Detailing & Tint"
            className="h-7 w-auto select-none"
            draggable={false}
          />
          <div className="text-white/40 font-mono-alt text-[9px] tracking-[0.2em]">CRM · STUDIO</div>
        </button>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3">
          {NAV.filter((item) => !item.roles || item.roles.includes(user?.role)).map(({ to, label, icon: Icon, exact, highlight, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              onClick={onClose}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 mx-3 px-3 py-[6px] rounded-lg text-[13px] transition-all ${
                  isActive
                    ? "bg-[#1F5EFF] text-white shadow-lg shadow-[#1F5EFF]/20"
                    : highlight
                    ? "text-[#1F5EFF] hover:bg-[#1F5EFF]/10 font-semibold"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
              <span>{label}</span>
              {highlight && (
                <span className="ml-auto text-[9px] font-mono-alt bg-[#1F5EFF]/20 text-[#1F5EFF] px-1.5 py-0.5 rounded">
                  QUICK
                </span>
              )}
              {badge && leadCount > 0 && (
                <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-[#1F5EFF] text-white text-[10px] font-bold flex items-center justify-center animate-pulse"
                  data-testid="nav-leads-badge">
                  {leadCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User footer */}
        <div className="border-t border-white/10 p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-white/20 to-white/5 border border-white/10 flex items-center justify-center text-white text-xs font-semibold">
            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-[13px] truncate">{user?.name || user?.email}</div>
            <div className="font-mono-alt text-[9px] text-white/40 uppercase">{user?.role}</div>
          </div>
          <button
            data-testid="crm-change-password"
            onClick={() => setPwOpen(true)}
            className="text-white/50 hover:text-white p-2 rounded-lg hover:bg-white/5"
            title="Change password"
          >
            <KeyRound className="w-4 h-4" />
          </button>
          <button
            data-testid="crm-logout"
            onClick={logout}
            className="text-white/50 hover:text-white p-2 rounded-lg hover:bg-white/5"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>
    </>
  );
}
